import {
  ResponsiveTable,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useTranslation,
} from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { OverviewInvoiceDue, OverviewStats } from '../overviewTypes.js';

import { HomeCard } from './HomeCard.js';

const MIN_BAR_PCT = 8;

function statusLabelKey(status: string): string {
  if (status === 'Overdue') return 'home:invoicesDue.overdue';
  if (status === 'Partial') return 'home:invoicesDue.partial';
  return 'home:invoicesDue.unpaid';
}

export function InvoicesDueQueue({
  invoices,
  stats,
  money,
  formatDate,
}: {
  invoices: OverviewInvoiceDue[];
  stats: OverviewStats;
  money: (value: number) => string;
  formatDate: (date: string) => string;
}) {
  const { t } = useTranslation();
  const overdueCount = stats.overdueInvoiceCount;
  const dueCount = Math.max(stats.invoicesDueCount - overdueCount, 0);
  const totalCount = Math.max(stats.invoicesDueCount, 1);
  const overduePct =
    overdueCount > 0 ? Math.max((overdueCount / totalCount) * 100, MIN_BAR_PCT) : 0;
  const duePct = dueCount > 0 ? Math.max((dueCount / totalCount) * 100, MIN_BAR_PCT) : 0;

  return (
    <HomeCard
      headingId="home-invoices-due-heading"
      title={t('home:invoicesDue.heading')}
      description={t('home:invoicesDue.rollup', {
        total: money(stats.invoicesDueTotal),
        count: stats.invoicesDueCount,
      })}
      action={
        <div className="min-w-44 shrink-0 rounded-lg border border-border bg-secondary/50 p-3">
          <div className="mb-2 flex items-center justify-between gap-3 text-xs">
            <span className="font-medium text-muted-foreground">
              {t('home:invoicesDue.statusStrip')}
            </span>
            <span className="font-semibold tabular-nums text-foreground">
              {t('home:invoicesDue.countStrip', {
                overdue: overdueCount,
                due: dueCount,
              })}
            </span>
          </div>
          {/*
            `role="img"` because the bar IS the picture and its label is the whole of what it
            says. A bare labelled <div> has no role for the label to attach to, so the sentence
            was being dropped by the very readers it was written for.
          */}
          <div
            role="img"
            className="flex h-2 overflow-hidden rounded-full bg-muted"
            aria-label={t('home:invoicesDue.statusStripLabel', {
              overdue: overdueCount,
              due: dueCount,
            })}
          >
            {overduePct > 0 ? (
              <span className="bg-destructive" style={{ width: `${overduePct}%` }} />
            ) : null}
            {duePct > 0 ? (
              <span className="bg-muted-foreground/45" style={{ width: `${duePct}%` }} />
            ) : null}
          </div>
        </div>
      }
      status={invoices.length === 0 ? 'empty' : 'ready'}
      emptyText={t('home:invoicesDue.empty')}
    >
      <div className="mt-3">
        <ResponsiveTable>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('home:invoicesDue.customer')}</TableHead>
                <TableHead>{t('home:invoicesDue.status')}</TableHead>
                <TableHead>{t('home:invoicesDue.dueDate')}</TableHead>
                <TableHead className="text-right">{t('home:invoicesDue.balance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} className="relative">
                  <TableCell>
                    <Link
                      to="/invoices"
                      className="absolute inset-0 z-10 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="sr-only">
                        {t('home:invoicesDue.openInvoice', { number: invoice.number })}
                      </span>
                    </Link>
                    <div className="min-w-0">
                      {/* An invoice with no customer and no job used to print its number on both
                          lines. Say what is missing; the number is on the line below. */}
                      <div
                        className={
                          invoice.customer || invoice.jobName
                            ? 'truncate font-medium text-foreground'
                            : 'truncate font-medium text-muted-foreground'
                        }
                      >
                        {invoice.customer || invoice.jobName || t('home:invoicesDue.noCustomer')}
                      </div>
                      <div className="truncate text-muted-foreground text-xs">
                        {invoice.jobName
                          ? t('home:invoicesDue.invoiceJob', {
                              number: invoice.number,
                              jobName: invoice.jobName,
                            })
                          : invoice.number}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill tone={invoice.status === 'Overdue' ? 'danger' : 'neutral'}>
                      {t(statusLabelKey(invoice.status))}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(invoice.dueDate)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums text-foreground">
                    {money(invoice.balance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </div>
    </HomeCard>
  );
}
